import React, { useEffect, useRef, useState, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import { apiUrls } from '../utils/apiUrls';
import { callAPI } from '../utils/apiUtils';
import { toast } from 'react-toastify';
import ProductEditModal from '../components/modals/ProductEditModal';
import WeightedProductEditModal from '../components/modals/WeightedProductEditModal';
import { syncMenu } from '../utils/syncMenu';
import ProductAiAssistant from '../components/product/ProductAiAssistant';
import GenieTabButton from '../components/GenieTabButton';
import AiActivityLog from '../components/product/AiActivityLog';
import AvailabilitySelect, { AVAILABILITY_OPTIONS } from '../components/product/AvailabilitySelect';
import StockPanel from '../components/product/StockPanel';
import '../styles/hardwarePage.css';

const ToggleSwitch = ({ checked, onChange }) => (
  <label className="toggleSwitch">
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="toggleSlider" />
  </label>
);

const TABS = [
  { key: 'products', label: 'Products' },
  { key: 'stock', label: 'Stock' },
  { key: 'categories', label: 'Categories' },
  { key: 'subcategories', label: 'Sub Categories' },
  { key: 'weighted', label: 'Price by Weight' },
  { key: 'ailog', label: 'AI Activity Log' },
  { key: 'ai', label: 'AI Assistant' },
];

// The two AI tabs manage their own content and have nothing to search — the shared search box and
// the initial products/categories fetch are both pointless there.
const TABS_WITHOUT_SEARCH = ['ai', 'ailog'];

const priceRangeLabel = (sizes) => {
  if (!sizes || sizes.length === 0) return '-';
  const prices = sizes.map((s) => s.dine_in_price).filter((p) => p !== null && p !== undefined);
  if (prices.length === 0) return '-';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? `$${min.toFixed(2)}` : `$${min.toFixed(2)} - $${max.toFixed(2)}`;
};

const weightedPriceLabel = (product) => `$${Number(product.price_per_unit).toFixed(2)} / ${product.pricing_unit_denominator}${product.weight_unit}`;

const ProductManagementPage = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [weightedProducts, setWeightedProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [editingWeightedProduct, setEditingWeightedProduct] = useState(null);
  const [isAddingWeightedProduct, setIsAddingWeightedProduct] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Any successful save below flips this so the ordering screens' menu cache (built by "Sync
  // Menu", read once on mount by Takeaway.js) doesn't silently drift out of date - staff would
  // otherwise have to remember to click Sync Menu themselves after every change made here. A ref
  // (not state) so the unmount effect's closure always sees the latest value without needing to
  // be re-created on every change.
  const hasUnsyncedChangesRef = useRef(false);

  useEffect(() => {
    return () => {
      if (hasUnsyncedChangesRef.current) {
        syncMenu().catch((err) => console.error('Auto sync-on-leave failed', err));
      }
    };
  }, []);

  // --topSpace (used for .pageContent's padding-top) includes extra breathing room beyond the
  // fixed header's real height, so sticking the tab bar at that offset leaves a thin unmasked
  // gap between the header's true bottom edge and the tab bar - the scrolling list peeks through
  // it. Measure the header's actual rendered height instead so the tab bar sits flush against it.
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const measureHeader = () => {
      const header = document.querySelector('.headerSection');
      if (header) setHeaderHeight(header.getBoundingClientRect().height);
    };
    measureHeader();
    window.addEventListener('resize', measureHeader);
    return () => window.removeEventListener('resize', measureHeader);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, subcategoriesRes] = await Promise.all([
        callAPI(apiUrls.PRODUCT_MANAGEMENT_PRODUCTS, {}, 'GET'),
        callAPI(apiUrls.PRODUCT_MANAGEMENT_CATEGORIES, {}, 'GET'),
        callAPI(apiUrls.PRODUCT_MANAGEMENT_SUBCATEGORIES, {}, 'GET'),
      ]);
      setProducts(productsRes.data?.data || []);
      setCategories(categoriesRes.data?.data || []);
      setSubcategories(subcategoriesRes.data?.data || []);
    } catch (err) {
      console.error('Failed to load product management data', err);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }

    // Kept as its own request outside the try/catch above - the "Add Product" size picker is a
    // nice-to-have on top of the core products/categories screen, so a sizes-fetch failure
    // shouldn't block the rest of the page from loading (it just falls back to the flat Price
    // field, same as a restaurant with no sizes configured at all).
    try {
      const sizesRes = await callAPI(apiUrls.PRODUCT_MANAGEMENT_SIZES, {}, 'GET');
      setAvailableSizes(sizesRes.data?.data || []);
    } catch (err) {
      console.error('Failed to load serving sizes', err);
    }

    // Also its own request, same reasoning - "Price by Weight" is its own separate concern
    // (weighted_products table, not 99_menuitems), so a failure here shouldn't block the rest
    // of the page.
    try {
      const weightedRes = await callAPI(apiUrls.WEIGHTED_PRODUCTS, {}, 'GET');
      setWeightedProducts(weightedRes.data?.data?.products || []);
    } catch (err) {
      console.error('Failed to load weighted products', err);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleToggleProductStatus = async (product) => {
    const newStatus = product.status === 1 ? 0 : 1;
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, status: newStatus } : p)));
    try {
      await callAPI(apiUrls.PRODUCT_MANAGEMENT_UPDATE_PRODUCT, {}, 'POST', { id: product.id, status: newStatus }, {});
      hasUnsyncedChangesRef.current = true;
    } catch (err) {
      console.error('Failed to update product status', err);
      toast.error('Failed to update product status');
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, status: product.status } : p)));
    }
  };

  const handleToggleCategoryStatus = async (category) => {
    const newStatus = category.status === 1 ? 0 : 1;
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, status: newStatus } : c)));
    try {
      await callAPI(apiUrls.PRODUCT_MANAGEMENT_UPDATE_CATEGORY, {}, 'POST', { id: category.id, status: newStatus }, {});
      hasUnsyncedChangesRef.current = true;
    } catch (err) {
      console.error('Failed to update category status', err);
      toast.error('Failed to update category status');
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, status: category.status } : c)));
    }
  };

  const handleToggleSubcategoryStatus = async (subcategory) => {
    const newStatus = subcategory.status === 1 ? 0 : 1;
    setSubcategories((prev) => prev.map((s) => (s.id === subcategory.id ? { ...s, status: newStatus } : s)));
    try {
      await callAPI(apiUrls.PRODUCT_MANAGEMENT_UPDATE_SUBCATEGORY, {}, 'POST', { id: subcategory.id, status: newStatus }, {});
      hasUnsyncedChangesRef.current = true;
    } catch (err) {
      console.error('Failed to update sub-category status', err);
      toast.error('Failed to update sub-category status');
      setSubcategories((prev) => prev.map((s) => (s.id === subcategory.id ? { ...s, status: subcategory.status } : s)));
    }
  };

  // Sold out is applied live (the ordering screen overlays /sold-out on its cached menu), so unlike
  // the Enabled toggles above this deliberately does not mark the menu as needing a sync.
  const AVAILABILITY_TARGETS = {
    product: { setList: setProducts, successLabel: 'Product' },
    category: { setList: setCategories, successLabel: 'Category' },
    subcategory: { setList: setSubcategories, successLabel: 'Sub category' },
  };

  const handleSetAvailability = async (type, item, mode) => {
    const { setList } = AVAILABILITY_TARGETS[type];
    const previous = { availability: item.availability || 'available', sold_out_until: item.sold_out_until || null };
    const patch = (fields) => setList((prev) => prev.map((x) => (x.id === item.id ? { ...x, ...fields } : x)));

    patch({ availability: mode, sold_out_until: null });
    try {
      const res = await callAPI(apiUrls.PRODUCT_MANAGEMENT_SET_AVAILABILITY, {}, 'POST', { type, id: item.id, mode }, {});
      const saved = res.data?.data;
      if (saved) patch({ availability: saved.availability, sold_out_until: saved.sold_out_until });
      const label = AVAILABILITY_OPTIONS.find((o) => o.value === mode)?.label.toLowerCase();
      toast.success(`${item.name} set to ${label}`);
    } catch (err) {
      console.error('Failed to update availability', err);
      toast.error('Failed to update availability');
      patch(previous);
    }
  };

  // A product is also sold out when its category or sub-category is - the server cascades this, so
  // show where it comes from rather than a product that says "Available" but can't be ordered.
  const soldOutInheritedFrom = (product) => {
    if ((product.availability || 'available') !== 'available') return null;
    const isOut = (x) => x && (x.availability || 'available') !== 'available';
    if (isOut(categories.find((c) => String(c.id) === String(product.cuisine_id)))) return 'category';
    if (isOut(subcategories.find((c) => String(c.id) === String(product.category_id)))) return 'subcategory';
    return null;
  };

  const handleProductSaved = (savedProduct, isNew) => {
    if (isNew) {
      setProducts((prev) => [savedProduct, ...prev]);
      setIsAddingProduct(false);
    } else {
      setProducts((prev) => prev.map((p) => (p.id === savedProduct.id ? savedProduct : p)));
      setEditingProduct(null);
    }
    hasUnsyncedChangesRef.current = true;
  };

  const handleToggleProductPopup = async (product) => {
    const next = !product.force_customization_popup;
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, force_customization_popup: next } : p)));
    try {
      await callAPI(apiUrls.PRODUCT_MANAGEMENT_UPDATE_PRODUCT, {}, 'POST', { id: product.id, force_customization_popup: next }, {});
      hasUnsyncedChangesRef.current = true;
    } catch (err) {
      console.error('Failed to update product customisation pop up flag', err);
      toast.error('Failed to update customisation pop up setting');
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, force_customization_popup: product.force_customization_popup } : p)));
    }
  };

  const handleToggleCategoryPopup = async (category) => {
    const next = !category.force_customization_popup;
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, force_customization_popup: next } : c)));
    try {
      await callAPI(apiUrls.PRODUCT_MANAGEMENT_UPDATE_CATEGORY, {}, 'POST', { id: category.id, force_customization_popup: next }, {});
      hasUnsyncedChangesRef.current = true;
    } catch (err) {
      console.error('Failed to update category customisation pop up flag', err);
      toast.error('Failed to update customisation pop up setting');
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, force_customization_popup: category.force_customization_popup } : c)));
    }
  };

  const handleToggleSubcategoryPopup = async (subcategory) => {
    const next = !subcategory.force_customization_popup;
    setSubcategories((prev) => prev.map((s) => (s.id === subcategory.id ? { ...s, force_customization_popup: next } : s)));
    try {
      await callAPI(apiUrls.PRODUCT_MANAGEMENT_UPDATE_SUBCATEGORY, {}, 'POST', { id: subcategory.id, force_customization_popup: next }, {});
      hasUnsyncedChangesRef.current = true;
    } catch (err) {
      console.error('Failed to update sub-category customisation pop up flag', err);
      toast.error('Failed to update customisation pop up setting');
      setSubcategories((prev) => prev.map((s) => (s.id === subcategory.id ? { ...s, force_customization_popup: subcategory.force_customization_popup } : s)));
    }
  };

  const handleToggleWeightedProductStatus = async (product) => {
    const newStatus = product.status === 1 ? 0 : 1;
    setWeightedProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, status: newStatus } : p)));
    try {
      await callAPI(apiUrls.WEIGHTED_PRODUCTS_UPDATE, {}, 'POST', { id: product.id, status: newStatus }, {});
    } catch (err) {
      console.error('Failed to update weighted product status', err);
      toast.error('Failed to update status');
      setWeightedProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, status: product.status } : p)));
    }
  };

  const handleWeightedProductSaved = (savedProduct, isNew) => {
    if (isNew) {
      setWeightedProducts((prev) => [savedProduct, ...prev]);
      setIsAddingWeightedProduct(false);
    } else {
      setWeightedProducts((prev) => prev.map((p) => (p.id === savedProduct.id ? savedProduct : p)));
      setEditingWeightedProduct(null);
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredProducts = normalizedSearch
    ? products.filter((p) => p.name?.toLowerCase().includes(normalizedSearch))
    : products;
  const filteredCategories = normalizedSearch
    ? categories.filter((c) => c.name?.toLowerCase().includes(normalizedSearch))
    : categories;
  const filteredSubcategories = normalizedSearch
    ? subcategories.filter((s) => s.name?.toLowerCase().includes(normalizedSearch))
    : subcategories;
  const filteredWeightedProducts = normalizedSearch
    ? weightedProducts.filter((p) => p.name?.toLowerCase().includes(normalizedSearch))
    : weightedProducts;

  return (
    <MainLayout>
      <div className="pageContent">
        <div className="container">
          <div
            className="hardwareTabBar"
            role="tablist"
            style={{
              position: 'sticky',
              top: headerHeight,
              zIndex: 10,
              background: 'var(--black)',
              paddingTop: 12,
              paddingBottom: 12,
            }}
          >
            {TABS.map((tab) => (
              // The AI Assistant gets the shared genie pill rather than a plain tab button, so it
              // looks the same here as it does on the Roster screen.
              tab.key === 'ai' ? (
                <GenieTabButton
                  key={tab.key}
                  active={activeTab === tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSearchTerm('');
                  }}
                />
              ) : (
                <button
                  key={tab.key}
                  type="button"
                  className={`hardwareTabBtn ${activeTab === tab.key ? 'active' : ''}`}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSearchTerm('');
                  }}
                >
                  {tab.label}
                </button>
              )
            ))}
          </div>

          {!TABS_WITHOUT_SEARCH.includes(activeTab) && (
            <div className="mb-3" style={{ maxWidth: 320 }}>
              <input
                type="text"
                className="form-control"
                placeholder={`Search ${TABS.find((t) => t.key === activeTab)?.label.toLowerCase()}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}

          {/* Rendered ahead of the loading gate: both AI tabs fetch their own data and shouldn't
              sit behind the products/categories load they don't use. */}
          {activeTab === 'ai' && (
            <ProductAiAssistant
              onApplied={() => {
                // An applied plan can have changed anything on this screen, and it also leaves the
                // ordering screens' menu cache stale in exactly the way a manual edit does.
                hasUnsyncedChangesRef.current = true;
                loadAll();
              }}
            />
          )}
          {activeTab === 'ailog' && <AiActivityLog />}

          {TABS_WITHOUT_SEARCH.includes(activeTab) ? null : loading ? (
            <p>Loading...</p>
          ) : (
            <>
              {activeTab === 'products' && (
                <div className="printerCard" style={{ overflowX: 'auto' }}>
                  <div className="d-flex justify-content-end mb-2">
                    <button className="btn btn-primary btn-sm" onClick={() => setIsAddingProduct(true)}>
                      + Add Product
                    </button>
                  </div>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Category</th>
                        <th>Sub Category</th>
                        <th>Price</th>
                        <th>Enabled</th>
                        <th>Availability</th>
                        <th>Customisation Pop Up</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.id}>
                          <td>{product.name}</td>
                          <td>{product.cuisine_name || '-'}</td>
                          <td>{product.category_name || '-'}</td>
                          <td>{priceRangeLabel(product.sizes)}</td>
                          <td>
                            <ToggleSwitch
                              checked={product.status === 1}
                              onChange={() => handleToggleProductStatus(product)}
                            />
                          </td>
                          <td>
                            <AvailabilitySelect
                              value={product.availability || 'available'}
                              inheritedFrom={soldOutInheritedFrom(product)}
                              onChange={(mode) => handleSetAvailability('product', product, mode)}
                            />
                          </td>
                          <td>
                            <ToggleSwitch
                              checked={!!product.force_customization_popup}
                              onChange={() => handleToggleProductPopup(product)}
                            />
                          </td>
                          <td>
                            <button className="btn btn-sm btn-secondary" onClick={() => setEditingProduct(product)}>
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                      {filteredProducts.length === 0 && (
                        <tr><td colSpan={8}>No products found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'stock' && <StockPanel searchTerm={searchTerm} />}

              {activeTab === 'categories' && (
                <div className="printerCard" style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Enabled</th>
                        <th>Availability</th>
                        <th>Customisation Pop Up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategories.map((category) => (
                        <tr key={category.id}>
                          <td>{category.name}</td>
                          <td>
                            <ToggleSwitch
                              checked={category.status === 1}
                              onChange={() => handleToggleCategoryStatus(category)}
                            />
                          </td>
                          <td>
                            <AvailabilitySelect
                              value={category.availability || 'available'}
                              onChange={(mode) => handleSetAvailability('category', category, mode)}
                            />
                          </td>
                          <td>
                            <ToggleSwitch
                              checked={!!category.force_customization_popup}
                              onChange={() => handleToggleCategoryPopup(category)}
                            />
                          </td>
                        </tr>
                      ))}
                      {filteredCategories.length === 0 && (
                        <tr><td colSpan={4}>No categories found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'subcategories' && (
                <div className="printerCard" style={{ overflowX: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Sub Category</th>
                        <th>Category</th>
                        <th>Enabled</th>
                        <th>Availability</th>
                        <th>Customisation Pop Up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubcategories.map((subcategory) => (
                        <tr key={subcategory.id}>
                          <td>{subcategory.name}</td>
                          <td>{subcategory.cuisine_name || '-'}</td>
                          <td>
                            <ToggleSwitch
                              checked={subcategory.status === 1}
                              onChange={() => handleToggleSubcategoryStatus(subcategory)}
                            />
                          </td>
                          <td>
                            <AvailabilitySelect
                              value={subcategory.availability || 'available'}
                              onChange={(mode) => handleSetAvailability('subcategory', subcategory, mode)}
                            />
                          </td>
                          <td>
                            <ToggleSwitch
                              checked={!!subcategory.force_customization_popup}
                              onChange={() => handleToggleSubcategoryPopup(subcategory)}
                            />
                          </td>
                        </tr>
                      ))}
                      {filteredSubcategories.length === 0 && (
                        <tr><td colSpan={5}>No sub-categories found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'weighted' && (
                <div className="printerCard" style={{ overflowX: 'auto' }}>
                  <div className="d-flex justify-content-end mb-2">
                    <button className="btn btn-primary btn-sm" onClick={() => setIsAddingWeightedProduct(true)}>
                      + Add Weighted Product
                    </button>
                  </div>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Price</th>
                        <th>Enabled</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWeightedProducts.map((product) => (
                        <tr key={product.id}>
                          <td>{product.name}</td>
                          <td>{weightedPriceLabel(product)}</td>
                          <td>
                            <ToggleSwitch
                              checked={product.status === 1}
                              onChange={() => handleToggleWeightedProductStatus(product)}
                            />
                          </td>
                          <td>
                            <button className="btn btn-sm btn-secondary" onClick={() => setEditingWeightedProduct(product)}>
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                      {filteredWeightedProducts.length === 0 && (
                        <tr><td colSpan={4}>No weighted products found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <ProductEditModal
          isOpen={isAddingProduct || !!editingProduct}
          product={editingProduct}
          categories={categories}
          subcategories={subcategories}
          availableSizes={availableSizes}
          onClose={() => {
            setIsAddingProduct(false);
            setEditingProduct(null);
          }}
          onSaved={handleProductSaved}
        />

        <WeightedProductEditModal
          isOpen={isAddingWeightedProduct || !!editingWeightedProduct}
          product={editingWeightedProduct}
          onClose={() => {
            setIsAddingWeightedProduct(false);
            setEditingWeightedProduct(null);
          }}
          onSaved={handleWeightedProductSaved}
        />
      </div>
    </MainLayout>
  );
};

export default ProductManagementPage;
